import { setProp } from '@shared/helpers/operators';
import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import { Component, Prop } from 'vue-property-decorator';
import EntityTableProvider from '@shared/components/tables/entity-table-provider/entity-table-provider.vue';
import EntityTable from '@shared/components/tables/entity-table/entity-table.vue';
import BpcPageTitle from '@shared/layouts/components/page-title/page-title.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import EntityDeleteModalProvider from '@shared/components/modals/entity-delete-modal-provider/entity-delete-modal-provider.vue';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import { ValidationObserver } from 'vee-validate';
import { mixins } from 'vue-class-component';
import AbstractTable from '@shared/components/tables/abstract-table/abstract-table';
import StdFilter from '@shared/components/filters/std-filter/std-filter.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import { Enrollment } from '@modules/activities/interfaces/enrollment';
import BpcDrawerModal from '@shared/components/modals/drawer-modal/drawer-modal.vue';
import CustomFilterSelector from '@shared/components/filters/custom-filter-selector/custom-filter-selector.vue';
import ExtendedFilter from '@shared/components/filters/extended-filter/extended-filter.vue';
import AuthorizationHelper from '@auth/services/authorization.helper';
import { ConfirmationModalConfig } from '@shared/interfaces/confirmation-modal-config';
import { ActivityGroup } from '@activities/interfaces/activity-group';
import EntityFormProvider from '@shared/components/forms/auto-form/entity-form-provider/entity-form-provider';
import BulkMoveToTrashModal from '@shared/components/modals/bulk-move-to-trash-modal/bulk-move-to-trash-modal.vue';
import SendMessageUsingTemplateMixin from '@shared/components/tables/abstract-table/send-message-using-template-mixing';
import DownloadPdfUsingTemplateMixin from '@shared/components/tables/abstract-table/download-pdf-using-template-mixing';
import BulkRecoverModal from '@shared/components/modals/bulk-recover-modal/bulk-recover-modal.vue';
import ModulesHelper from '@core/services/modules.helper';
import { FilterObject } from '@shared/interfaces/config';
import moment from 'moment';
import TaxCertificateForm from '@tax-certificates/components/tax-certificate-form.vue';
import { TaxCertificateForm as FormModel } from '@tax-certificates/interfaces/tax-certificate-form';
import AddPaymentModal from '@accounting/components/modals/add-payment-modal/add-payment-modal.vue';
import { AccessControlBadge } from '@access-control/interfaces/access-control-badge';
import BpcAccessControlBadgeForm from '@access-control/components/access-control-badge-form.vue';
import BulkSendPaymentRequestModal from '@shared/components/modals/send-bulk-product-payment-request-message-drawer/send-bulk-product-payment-request-message-drawer.vue';
import BulkSendPaymentReminderModal from '@shared/components/modals/send-bulk-product-payment-reminder-message-drawer/send-bulk-product-payment-reminder-message-drawer.vue';

library.add(faSync);

@Component({
  name: 'bpc-enrollment-table',
  components: {
    'bc-tax-certificate-form': TaxCertificateForm,
    'bpc-entity-table-provider': EntityTableProvider,
    'bpc-entity-table': EntityTable,
    'bpc-page-title': BpcPageTitle,
    'font-awesome-icon': FontAwesomeIcon,
    'bpc-entity-delete-modal-provider': EntityDeleteModalProvider,
    'bpc-confirmation-modal': ConfirmationModal,
    'bpc-std-form': StdForm,
    'bpc-std-filter': StdFilter,
    ValidationObserver,
    BpcAccessControlBadgeForm,
    'bpc-drawer-modal': BpcDrawerModal,
    'bpc-custom-filter-selector': CustomFilterSelector,
    'bpc-extended-filter': ExtendedFilter,
    'bpc-bulk-move-to-trash-modal': BulkMoveToTrashModal,
    'bpc-bulk-recover-modal': BulkRecoverModal,
    'bc-add-payment-modal': AddPaymentModal,
    'bpc-bulk-send-payment-request-modal': BulkSendPaymentRequestModal,
    'bpc-bulk-send-payment-reminder-modal': BulkSendPaymentReminderModal,
  },
})
export default class EnrollmentTable extends mixins(
  AbstractTable,
  SendMessageUsingTemplateMixin,
  DownloadPdfUsingTemplateMixin,
) {
  declare $refs: AbstractTable['$refs'] & {
    confirmEnrollmentConfirmationModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    stdCancelForm: InstanceType<typeof StdForm> & {
      formModel: any;
      formProvider: EntityFormProvider;
    };
    taxCertificateForm: InstanceType<typeof TaxCertificateForm> & {
      saveEntity(): void;
    };
  };

  @Prop(String) declare readonly displayConfigName: string;
  @Prop({ type: Boolean, default: false }) readonly showActionButtonText: boolean;
  @Prop({ type: Boolean, default: false }) declare readonly isInput: boolean;
  @Prop({ type: Boolean, default: true }) readonly showTableHeader: boolean;
  @Prop(String) readonly fieldForStorage: string;
  @Prop({ type: String, default: 'cancel-enrollments' }) readonly cancelFormConfigName: string;
  @Prop({ type: Array, default: () => ['part1', 'part2'] })
  declare readonly selectConfigName: string[];
  @Prop({ type: Array, default: () => ['type'] })
  declare readonly filtersExcluded: string[];
  @Prop({ type: String, default: 'edit-enrollment' }) readonly editFormConfigName: string;
  @Prop({ type: String, default: 'bulk-edit' }) readonly bulkEditFormConfigName: string;
  @Prop(Object) readonly externalFormModel: Record<string, any>;

  public entity: Enrollment = null;
  public entityName = 'enrollment';
  public entityNameToDownloadPDF = 'enrollment';
  public pluralEntityName = 'enrollments';
  public showDrawerSettingSection: boolean = false;
  public showSettingForm: boolean = false;
  public permissionEntityNameToUse = 'enrollments';
  public selectedRowEntity = null;
  public warningMessagesBeforeConfirmEnrollment: [] = [];
  public selectedActivityGroupToEnroll: ActivityGroup = null;
  public isCancelingEntity: boolean = false;
  public contactCantBeEnrolledMsg: string = '';
  // Add payment modal
  public showPaymentModal: boolean = false;

  //Bulk messages data
  bulkSelectedContactIds: number[] = [];
  bulkSelectedSubscriptionIds: number[] = [];
  public showBulkSendPaymentRequestModal = false;
  public showBulkSendPaymentReminderModal = false;

  onBulkSendPaymentRequest(selectedIds: number[]) {
    this.bulkSelectedSubscriptionIds = selectedIds;
    this.bulkSelectedContactIds = selectedIds
      .map(itemId => {
        const item = (this.$refs.provider.items as any).find(i => i.id === itemId);
        return item?.contact_id;
      })
      .filter(Boolean);
    this.showBulkSendPaymentRequestModal = true;
  }

  onCloseBulkSendPaymentRequestModal() {
    this.$refs.provider.getEntities(); // refresh table
    this.showBulkSendPaymentRequestModal = false;
  }

  onBulkSendPaymentReminder(selectedIds: number[]) {
    this.bulkSelectedSubscriptionIds = selectedIds;
    this.bulkSelectedContactIds = selectedIds
      .map(itemId => {
        const item = (this.$refs.provider.items as any).find(i => i.id === itemId);
        return item?.contact_id;
      })
      .filter(Boolean);

    this.showBulkSendPaymentReminderModal = true;
  }

  onCloseBulkSendPaymentReminderModal() {
    this.$refs.provider.getEntities(); // refresh table
    this.showBulkSendPaymentReminderModal = false;
  }

  public flagsToCheckBeforeLoadEntities: string[] = [
    'configLoaded',
    'filtersLoaded',
    'paginationLoaded',
    'orderLoaded',
    'urlParametersLoaded',
  ];

  public customPageActions = {
    bulkCancel: {
      activated: false,
      type: 'action',
      icon: 'ban', //'times',
      variant: 'danger',
      text: 'cancel',
      show: false,
      event: 'bulk-cancel',
      selectElements: true,
      tag: 'left',
      permissions: ['bulk_cancel'],
    },
    bulkActions: {
      activated: false,
      type: 'dropdown',
      icon: 'cubes',
      variant: 'info',
      text: 'bulk_actions',
      show: false,
      event: '',
      selectElements: true,
      tag: 'bulk',
      permissions: [],
      children: {
        bulkEdit: {
          activated: false,
          type: 'dropdown-item',
          icon: 'edit',
          variant: 'info',
          text: 'bulk-edit',
          show: true,
          event: 'bulk-edit',
          selectElements: true,
          tag: 'left',
          permissions: ['bulk_edit'],
          withoutDivider: false,
        },
        divider1: {
          activated: false,
          type: 'head',
          text: null,
          show: true,
          withoutDivider: false,
          permissions: [],
        },
        bulkPaymentRequest: {
          activated: false,
          type: 'dropdown-item',
          icon: 'envelope', //'times',
          variant: 'info',
          text: 'send_payment_request',
          show: false,
          event: 'bulk-send-payment-request',
          selectElements: true,
          tag: 'right',
          permissions: ['bulk_message'],
        },
        bulkPaymentReminder: {
          activated: false,
          type: 'dropdown-item',
          icon: 'envelope', //'times',
          variant: 'info',
          text: 'send_payment_reminder',
          show: false,
          event: 'bulk-send-payment-reminder',
          selectElements: true,
          tag: 'right',
          permissions: ['bulk_message'],
        },
        bulkSendMessageUsingTemplate: {
          activated: false,
          type: 'dropdown-item',
          icon: 'envelope', //'times',
          variant: 'info',
          text: 'send_message_using_template',
          show: false,
          event: 'bulk-send-message-using-template',
          selectElements: true,
          tag: 'right',
          permissions: ['bulk_message'],
          withoutDivider: false,
        },
        divider2: {
          activated: false,
          type: 'head',
          text: null,
          show: true,
          withoutDivider: false,
          permissions: [],
        },
        bulkSoftDelete: {
          activated: false,
          type: 'dropdown-item',
          icon: 'trash',
          variant: 'danger',
          text: 'soft-delete',
          show: false,
          event: 'bulk-soft-delete',
          selectElements: true,
          tag: 'left',
          permissions: ['bulk_delete'],
        },
      },
    },

    bulkDownloadPdfUsingTemplate: {
      activated: false,
      type: 'action',
      icon: 'download', //'times',
      variant: 'info',
      text: 'download_pdf_using_template',
      show: false,
      event: 'bulk-download-pdf-using-template',
      selectElements: true,
      tag: 'left',
      permissions: ['show'],
    },
    bulkSendMessageUsingTemplate: {
      activated: false,
      type: 'action',
      icon: 'envelope', //'times',
      variant: 'info',
      text: 'send_message_using_template',
      show: false,
      event: 'bulk-send-message-using-template',
      selectElements: true,
      tag: 'left',
      permissions: ['show'],
    },
  };

  customOnPageAction($event: string) {
    switch ($event) {
      case 'bulk-cancel':
        this.onOpenCancelModal();
        break;
      case 'bulk-download-pdf-using-template':
        this.onBulkDownloadPdfUsingTemplate(this.selection);
        break;
      case 'bulk-send-message-using-template':
        this.onBulkSendMessageUsingTemplate(this.selection);
        break;
      case 'bulk-send-payment-request':
        this.onBulkSendPaymentRequest(this.selection);
        break;
      case 'bulk-send-payment-reminder':
        this.onBulkSendPaymentReminder(this.selection);
        break;
    }
  }

  extendUpdatePageActions() {
    const activatedPageActions = this.config?.data?.activatedPageActions || [];

    if (this.config) {
      for (const activatedPageAction of activatedPageActions) {
        if (this.customPageActions[activatedPageAction]) {
          this.customPageActions[activatedPageAction].activated = true;
        }
      }
    }
    this.customPageActions.bulkCancel.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed;

    this.customPageActions.bulkDownloadPdfUsingTemplate.show =
      this.availableTemplatesForDownloadPdf.length &&
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed;

    // Bulk actions
    this.customPageActions.bulkActions.activated =
      this.tableIsNotFilteredByTrashedElements() &&
      activatedPageActions.includes('bulkActions') &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME) &&
      this.$modulesService.hasModuleActivated(ModulesHelper.ACCOUNTING_MODULE_NAME);

    this.customPageActions.bulkActions.show =
      this.tableIsNotFilteredByTrashedElements() &&
      activatedPageActions.includes('bulkActions') &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME) &&
      this.$modulesService.hasModuleActivated(ModulesHelper.ACCOUNTING_MODULE_NAME);

    this.customPageActions.bulkActions.children.bulkEdit.show =
      this.tableIsNotFilteredByTrashedElements() &&
      activatedPageActions.includes('bulkEdit') &&
      this.$authorizationService.hasPermissionForAction(
        this.permissionEntityNameToUse,
        'bulk_edit',
      );

    this.customPageActions.bulkActions.children.bulkSoftDelete.show =
      this.tableIsNotFilteredByTrashedElements() &&
      activatedPageActions.includes('bulkSoftDelete') &&
      this.$authorizationService.hasPermissionForAction(
        this.permissionEntityNameToUse,
        'bulk_delete',
      );

    // TODO: Add more checks when implementing this function
    this.customPageActions.bulkActions.children.bulkPaymentRequest.show =
      this.tableIsNotFilteredByTrashedElements() &&
      activatedPageActions.includes('bulkPaymentRequest') &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME) &&
      this.$modulesService.hasModuleActivated(ModulesHelper.ACCOUNTING_MODULE_NAME);

    // TODO: Add more checks when implementing this function
    this.customPageActions.bulkActions.children.bulkPaymentReminder.show =
      this.tableIsNotFilteredByTrashedElements() &&
      activatedPageActions.includes('bulkPaymentReminder') &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME) &&
      this.$modulesService.hasModuleActivated(ModulesHelper.ACCOUNTING_MODULE_NAME);

    this.customPageActions.bulkActions.children.bulkSendMessageUsingTemplate.show =
      this.tableIsNotFilteredByTrashedElements() &&
      activatedPageActions.includes('bulkSendMessageUsingTemplate') &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME);

    // Check Permissions
    for (const activatedPageAction of activatedPageActions) {
      if (
        this.customPageActions[activatedPageAction] &&
        this.customPageActions[activatedPageAction]['permissions'] &&
        this.customPageActions[activatedPageAction]['permissions'].length
      ) {
        const permissions = [];
        this.customPageActions[activatedPageAction]['permissions'].forEach(permissionName => {
          permissions.push(
            {
              name: AuthorizationHelper.getPermissionName(
                permissionName,
                (this.permissionEntityNameToUse
                  ? this.permissionEntityNameToUse
                  : this.pluralEntityName
                ).replace('-', '_'),
              ),
            },
            {
              name: AuthorizationHelper.getPermissionName(
                permissionName,
                (this.permissionEntityNameToUse
                  ? this.permissionEntityNameToUse
                  : this.pluralEntityName
                ).replace('-', '_'),
                true,
              ),
            },
          );
        });
        this.customPageActions[activatedPageAction][
          'activated'
        ] = this.$authorizationService.hasPermissions(permissions);
      }
    }
  }

  setEntityAttributesAfterSave() {
    const newEntity = {} as Enrollment;
    Object.keys(this.entity).forEach(key => {
      this.$set(newEntity, key, null);
    });

    this.$set(newEntity, 'status', this.entity.status);
    this.$set(this.$refs.stdForm, 'formModel', newEntity);

    this.showForm = true;
    this.showDrawerSection = true;
  }

  get pageActions() {
    // Invert the order
    return {
      ...this.customPageActions,
      ...this.defaultPageActions,
    };
  }

  get cancelModalTitle() {
    return this.$t('global.modals.bulk-cancel.title', {
      entityName: this.$t('global.models.' + this.entityName + '.name'),
      entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
      thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
      thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
    });
  }

  get cancelModalBody() {
    return this.$t('global.modals.bulk-cancel.body', {
      entityName: this.$t('global.models.' + this.entityName + '.name'),
      entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
      thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
      thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
    });
  }

  onOpenCancelModal() {
    this.$bvModal.show('modal-cancel-enrollments');
  }

  onCloseCancelModal() {
    this.selectedRowEntity = null;
    this.isCancelingEntity = false;
    this.$bvModal.hide('modal-cancel-enrollments');
  }

  onConfirmCancelModal() {
    this.isCancelingEntity = true;
    const basePath = this.$bpcApiRouteHelper.getEntityPath('enrollment');
    this.$bpcApi
      .post(basePath + '/bulk-cancel', {
        enrollmentIds: this.selection,
        cancel_reason: this.$authorizationService.hasPermissionForAction(
          this.permissionEntityNameToUse,
          'cancel',
        )
          ? this.$refs.stdCancelForm.formModel['cancel_reason']
          : 'customer',
        cancel_reason_description: this.$refs.stdCancelForm.formModel['cancel_reason_description'],
        notify_by_email:
          'notify_by_email' in this.$refs.stdCancelForm.formModel
            ? this.$refs.stdCancelForm.formModel['notify_by_email']
            : true,
      })
      .subscribe(
        res => {
          this.$refs.provider.getEntities();
          this.$bvModal.hide('modal-cancel-enrollments');
          this.isCancelingEntity = false;
        },
        error => {
          this.$bvModal.hide('modal-cancel-enrollments');
          this.isCancelingEntity = false;
        },
      );
  }

  public customRowActions = {
    sendMessageUsingTemplate: {
      activated: true,
      type: 'action',
      icon: 'envelope',
      show: true,
      event: 'send-message-using-template',
      text: 'send_message_using_template',
      permissions: ['show'],
    },
    downloadPdfUsingTemplate: {
      activated: true,
      type: 'action',
      icon: 'download',
      show: true,
      event: 'download-pdf-using-template',
      text: 'download_attestation_button_label',
      permissions: ['show'],
    },
    editTaxCertificate: {
      activated: true,
      type: 'action',
      icon: 'edit',
      show: true,
      event: 'edit-tax-certificate',
      text: 'edit_tax_certificate_button_label',
      permissions: ['store'],
    },
    downloadTaxCertificate: {
      activated: true,
      type: 'action',
      icon: 'download',
      show: true,
      event: 'download-tax-certificate',
      text: 'download_tax_certificate_button_label',
      permissions: ['show'],
    },
  };

  customOnRowAction($event: { event: string; item: any }) {
    switch ($event.event) {
      case 'cancel':
        this.onOpenCancelRowModal($event.item);
        break;
      case 'send-message-using-template':
        this.onSendMessageUsingTemplate($event.item.item);
        break;
      case 'download-pdf-using-template':
        this.onDownloadPdfUsingTemplate($event.item.item);
        break;
      case 'edit-tax-certificate':
        this.onFillTaxCertificate($event.item.item);
        break;
      case 'download-tax-certificate':
        this.onDownloadTaxCertificate($event.item.item?.tax_certificate_period?.tax_certificate_id);
        break;
      case 'pay-product':
        this.onPayProduct($event.item.item);
        break;
    }
  }

  onPayProduct(item) {
    this.entity = item;
    this.onOpenPaymentModal(item);
  }

  // Adapted specifically for attestations
  templateSelectorCustomFilters: FilterObject[] = [];
  downloadPdfTitle = 'global.download_attestation_modal_title';
  downloadPdfSelectLabel = 'global.download_attestation_select_label';
  downloadTemplateType = 'attestation';
  async onDownloadPdfUsingTemplate(item) {
    this.downloadPdfUsingTemplateSelectedItemIds = item?.id ? [item?.id] : [];
    this.entityNameToDownloadPDF = 'enrollment';
    this.downloadPdfTitle = 'global.download_attestation_modal_title';
    this.downloadPdfSelectLabel = 'global.download_attestation_select_label';
    this.downloadTemplateType = 'attestation';
    this.templateSelectorCustomFilters = [
      {
        filterField: 'enrollment_attestations',
        value: item.id,
      },
    ];
    this.downloadPdfUsingTemplateCleanErrors();
    this.openDownloadPdfUsingTemplateModal();
  }
  onDownloadTaxCertificate(taxCertificateId: number) {
    this.downloadPdfUsingTemplateSelectedItemIds = taxCertificateId ? [taxCertificateId] : [];
    this.entityNameToDownloadPDF = 'tax_certificate';
    this.downloadPdfTitle = 'global.download_tax_certificate_modal_title';
    this.downloadPdfSelectLabel = 'global.download_tax_certificate_select_label';
    this.downloadTemplateType = 'pdf';
    this.templateSelectorCustomFilters = [
      {
        filterField: 'status',
        value: 'active',
      },
    ];
    this.downloadPdfUsingTemplateCleanErrors();
    this.openDownloadPdfUsingTemplateModal();
  }
  async closeDownloadPdfUsingTemplateModal() {
    this.isDownloadingPdfUsingTemplate = false;
    this.showDownloadPdfUsingTemplateModal = false;
    this.templateSelectorCustomFilters = [];
    this.downloadPdfUsingTemplateCleanErrors();
  }

  get tenantId() {
    return this.$store.getters['tenant/selectedTenantId'];
  }

  showTaxCertificateForm: boolean = false;
  isSavingTaxCertificate: boolean = false;
  taxCertificateFormModel: FormModel = {
    id: null,
    code: null,
    center_id: this.tenantId,
    beneficiary_firstname: null,
    beneficiary_lastname: null,
    beneficiary_birthdate: null,
    beneficiary_national_number: null,
    beneficiary_street: null,
    beneficiary_street2: null,
    beneficiary_zip: null,
    beneficiary_city: null,
    beneficiary_country: null,
    contact_id: null,
    contact_firstname: null,
    contact_lastname: null,
    contact_birthdate: null,
    contact_national_number: null,
    contact_street: null,
    contact_street2: null,
    contact_zip: null,
    contact_city: null,
    contact_country: null,
    tax_certificate_periods: [
      {
        id: null,
        center_id: this.tenantId,
        tax_certificate_id: null,
        enrollment_id: null,
        start_date: null,
        end_date: null,
        num_days: null,
        price_per_day: null,
        paid_amount: null,
      },
    ],
  };

  onFillTaxCertificate(item) {
    this.taxCertificateFormModel.id = item.tax_certificate_period?.tax_certificate_id;
    this.taxCertificateFormModel.contact_id = item.contact_id;
    this.taxCertificateFormModel.tax_certificate_periods[0].enrollment_id = item.id;
    this.showTaxCertificateForm = true;
  }

  onCloseFillTaxCertificate() {
    this.showTaxCertificateForm = false;
    this.isSavingTaxCertificate = false;
    this.resetTaxCertificateFormModel();
  }

  onConfirmSaveTaxCertificate() {
    this.isSavingTaxCertificate = true;
    this.$refs.taxCertificateForm.saveEntity();
  }

  onSavedTaxCertificate() {
    this.$refs.provider.getEntities();
    this.onCloseFillTaxCertificate();
  }

  onErrorSavingTaxCertificate() {
    this.isSavingTaxCertificate = false;
  }

  resetTaxCertificateFormModel() {
    this.taxCertificateFormModel = {
      id: null,
      code: null,
      center_id: this.tenantId,
      beneficiary_firstname: null,
      beneficiary_lastname: null,
      beneficiary_birthdate: null,
      beneficiary_national_number: null,
      beneficiary_street: null,
      beneficiary_street2: null,
      beneficiary_zip: null,
      beneficiary_city: null,
      beneficiary_country: null,
      contact_id: null,
      contact_firstname: null,
      contact_lastname: null,
      contact_birthdate: null,
      contact_national_number: null,
      contact_street: null,
      contact_street2: null,
      contact_zip: null,
      contact_city: null,
      contact_country: null,
      tax_certificate_periods: [
        {
          id: null,
          center_id: this.tenantId,
          tax_certificate_id: null,
          enrollment_id: null,
          start_date: null,
          end_date: null,
          num_days: null,
          price_per_day: null,
          paid_amount: null,
        },
      ],
    };
  }

  extendUpdateRowActions(entity: any) {
    entity.actions.cancel.show =
      entity.status !== 'cancelled' &&
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      (this.$authorizationService.hasPermission('manage_enrollments') ||
        entity.enrollment_with_payment === false ||
        entity.options?.fixed_options.allow_cancel_enrollment_with_online_payment);

    entity.actions.sendMessageUsingTemplate.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME);

    entity.actions.downloadPdfUsingTemplate.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      moment().isAfter(entity.date) &&
      !!entity.has_attestations &&
      (!entity.orders_status ||
        entity.orders_status === 'paid' ||
        entity.orders_status === 'paid_with_subscription');

    entity.actions.editTaxCertificate.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      this.$modulesService.hasModuleActivated(ModulesHelper.TAX_CERTIFICATES_MODULE_NAME) &&
      !!entity.editable_tax_certificate &&
      this.$authorizationService.hasPermissionForAction('tax_certificates', 'store', true);

    entity.actions.downloadTaxCertificate.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      this.$modulesService.hasModuleActivated(ModulesHelper.TAX_CERTIFICATES_MODULE_NAME) &&
      !!entity.has_tax_certificate &&
      entity.status === 'approved' &&
      this.$authorizationService.hasPermissionForAction('tax_certificates', 'show');

    // Add Payment to product
    const canAddPayments = this.$authorizationService.hasPermissionForAction(
      'payments',
      'pay_product',
    );

    entity.actions.payProduct.show =
      this.tableIsNotFilteredByTrashedElements() &&
      canAddPayments &&
      entity.payment_status === 'unpaid' &&
      entity.status !== 'cancelled';

    entity.actions.payProduct.activated = !!entity.actions.payProduct.activated && canAddPayments;
  }

  get hasPermissionToCancel(): boolean {
    return this.$authorizationService.hasPermissionForAction(
      this.permissionEntityNameToUse,
      'cancel',
    );
  }

  onOpenCancelRowModal(item) {
    this.selectedRowEntity = item.item;
    this.$bvModal.show('modal-cancel-enrollments');
  }

  onConfirmCancelRowModal() {
    this.isCancelingEntity = true;
    const basePath = this.$bpcApiRouteHelper.getEntityPath('enrollment');
    const relativePath = '/cancel/' + this.selectedRowEntity.id;

    this.$bpcApi
      .put(basePath + relativePath, {
        id: this.selectedRowEntity.id,
        cancel_reason: this.$authorizationService.hasPermissionForAction(
          this.permissionEntityNameToUse,
          'cancel',
        )
          ? this.$refs.stdCancelForm.formModel['cancel_reason']
          : 'customer',
        cancel_reason_description: this.$refs.stdCancelForm.formModel['cancel_reason_description'],
        notify_by_email:
          'notify_by_email' in this.$refs.stdCancelForm.formModel
            ? this.$refs.stdCancelForm.formModel['notify_by_email']
            : true,
      })
      .subscribe(
        res => {
          this.$refs.provider.getEntities();
          this.$bvModal.hide('modal-cancel-enrollments');
          this.isCancelingEntity = false;
          this.selectedRowEntity = null;
        },
        error => {
          this.$bvModal.hide('modal-cancel-enrollments');
          this.isCancelingEntity = false;
          this.selectedRowEntity = null;
        },
      );
  }

  // TODO: fix bug with required fields in config and remove enrollmentFormConfigName()
  get enrollmentFormConfigName(): string {
    return !this.formConfigName || this.formConfigName == 'default'
      ? 'default-with-required-activity-group'
      : this.formConfigName;
  }

  get titleAddAccessControlBadgeModal(): string {
    return (
      this.$t('global.modals.create') + ' ' + this.$t('global.models.access-control-badge.name')
    );
  }

  get initialAccessControlBadgeData(): Partial<AccessControlBadge> {
    return {
      status: 'active',
      contact_id: Number(this.entity?.contact_id),
      type: 'rfid',
    };
  }
  timestamp = moment().unix();

  get accessControlBadgeFormCancelPortalTarget() {
    return `access-control-badge-form-cancel-action-${this.timestamp}`;
  }

  get accessControlBadgeFormSavePortalTarget() {
    return `access-control-badge-form-actions-${this.timestamp}`;
  }

  onSavedAccessControlBadge($event) {
    this.$root.$emit('new-badge-created-from-enrollment', $event.data);
  }

  onCloseAccessControlBadgeModal() {
    this.$bvModal.hide('modal-add-access-control-badge');
  }

  get hasAccessControlModuleActivated() {
    return this.$modulesService.hasModuleActivated(ModulesHelper.ACCESS_CONTROL_MODULE_NAME);
  }

  get canCreateAccessControlBadges() {
    const permissionName = AuthorizationHelper.getPermissionName('store', 'access_control_badges');
    return this.$authorizationService.hasPermissions([permissionName]);
  }

  onClickCreateAccessControlBadge() {
    this.$bvModal.show('modal-add-access-control-badge');
  }

  isSavingAccessControlBadge: boolean = false;
  labelSaveAccessControlBadgeButton: string =
    'global.access_control_badge.label_to_add_from_contact';

  async onFormSave(next: string = ''): Promise<void> {
    this.isSavingForm = true;
    this.nextAction = next;

    if (this.isInput) {
      const filter = this.filters.find(filter => filter['filterField'] == this.fieldForStorage);
      if (filter['value']) {
        this.$refs.stdForm.formModel[filter['filterField']] = filter['value'];
      }
      this.$refs.stdForm.formModel['form_config_name'] = this.enrollmentFormConfigName.replaceAll(
        '-',
        '_',
      );
    }

    this.$refs.stdForm.saveEntity();
  }

  confirmEnrollmentConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: 'confirm-enrollment-modal',
      title: this.$t('global.modals.enroll-confirmation.title'),
      body: this.$t('global.modals.enroll-confirmation.body', {
        activity_group_name: this.selectedActivityGroupToEnroll?.name,
      }) as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.confirm') as string,
    };
  }

  confirmEnrollment(): void {
    this.$refs.confirmEnrollmentConfirmationModal.onCloseModal();
    this.isSavingForm = true;
    this.$refs.stdForm.saveEntity();
  }

  // TODO: JJFC is this in use?
  async canBeEnrolled(entity: AnyEntity): Promise<object> {
    const canBeEnrolledPath = `${this.$bpcApiRouteHelper.getEntityPath(
      'enrollment',
    )}/can-be-enrolled`;
    return this.$bpcApi.post(canBeEnrolledPath, entity).toPromise();
  }

  async canBeEnrolledByAge(enrolment: AnyEntity) {
    this.contactCantBeEnrolledMsg = '';
    const canBeEnrolledPath = `${this.$bpcApiRouteHelper.getEntityPath(
      'enrollment',
    )}/can-be-enrolled-by-age`;
    this.$bpcApi
      .post(canBeEnrolledPath, {
        contact_id: enrolment.contact.id,
        activity_group_id: enrolment.activityGroup.id,
      })
      .subscribe(
        res => {
          if (!res.data.canBeEnrolled) {
            this.contactCantBeEnrolledMsg = res.data.message;
          }
          console.log('res:', res.data);
        },
        error => {
          this.$bpcMessage.showErrorMessage({ text: 'global.generic.error' });
        },
      );
  }

  async onFormSaveAndPay(): Promise<void> {
    await this.onFormSave('pay');
  }

  onModelLoaded($event) {
    this.$emit('model-loaded');
  }

  /* Lifecycle hooks */

  created() {
    // Remove default actions to be managed in other position (dropdown more actions)
    delete this.defaultPageActions.bulkSoftDelete;
    delete this.defaultPageActions.bulkEdit;
  }
  mounted() {
    this.exportCheckoutModalId = this.generateUUIDv4();
    this.$root.$on('refresh-table-after-add-orders-to-pos', () => {
      this.refreshEntitiesAfterOrdersAddedToPosCheckout();
    });
    this.$root.$on('updateEnrollmentPrice', event => {
      this.setEnrollmentPrice();
    });
    this.$root.$on('updateAttendanceDaysWhenChangeGroup', event => {
      this.$refs.stdForm.formModel.attendance_days = event?.num_days;
    });

    this.$root.$on('updateCheckAgeLimits', val => {
      this.contactCantBeEnrolledMsg = '';
      const enrollment = this.$refs.stdForm?.formModel;
      if (enrollment?.activityGroup?.enable_check_on_age) {
        this.canBeEnrolledByAge(enrollment);
      }
      console.log('enrollment', enrollment);
    });

    const templatesRequest = [
      this.getAllowedTemplatesForSendMessage(),
      this.getAllowedTemplatesForDownloadPdf(),
    ];

    Promise.all(templatesRequest).then(res => {
      this.updateRowActions();
      this.updatePageActions();
    });
  }

  // Override to be able to manage the new position of some default actions
  updatePageActions() {
    if (this.config) {
      for (const activatedPageAction of this.config.data.activatedPageActions) {
        if (this.pageActions[activatedPageAction]) {
          this.pageActions[activatedPageAction].activated = true;
        }
      }
    }
    this.pageActions.bulkDelete.show =
      this.$refs.provider &&
      (this.$refs.provider.queryConfig.params.status === 'trash' ||
        this.$refs.provider.queryConfig.params.onlyTrashed)
        ? true
        : false;
    this.pageActions.bulkRecover.show =
      this.$refs.provider &&
      (this.$refs.provider.queryConfig.params.status === 'trash' ||
        this.$refs.provider.queryConfig.params.onlyTrashed)
        ? true
        : false;

    // POS Checkout
    const hasPosCheckoutPermission = this.$authorizationService.hasPermissionForAction(
      'pos_sessions',
      'bulk_pos_checkout',
      true,
    );
    this.pageActions.bulkPosCheckout.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      this.$refs.provider.items?.find(item => item['orders']?.length > 0) &&
      this.$refs.provider.items?.find(item =>
        item['orders'].some(
          order =>
            order.status == 'waiting' ||
            (order.type === 'attendee' && order.status === 'shopping_cart'),
        ),
      ) &&
      hasPosCheckoutPermission &&
      !!this.openPosSession;

    // Check Permissions
    const activatedPageActions = this.config?.data?.activatedPageActions || [];
    for (const activatedPageAction of activatedPageActions) {
      if (
        this.pageActions[activatedPageAction] &&
        this.pageActions[activatedPageAction]['permissions']
      ) {
        this.checkPermission(activatedPageAction);
      }
    }
    this.extendUpdatePageActions();
  }

  destroyed() {
    if (this.$refs.entityTable) {
      this.$refs.entityTable.$off('on-click-display');
    }
    this.$root.$off('refresh-table-after-add-orders-to-pos');
    this.$root.$off('updateActivityGroup');
    this.$root.$off('updateAttendanceDaysWhenChangeGroup');
    this.$root.$off('updateCheckAgeLimits');
  }
  // updated() {}
  // destroyed() {}

  private getPriceParamsStringId() {
    return [
      this.$refs.stdForm?.formModel?.date,
      this.$refs.stdForm?.formModel?.article,
      this.$refs.stdForm?.formModel?.price_category,
    ].join(',');
  }

  setStdFormData(index, value) {
    setProp(this.$refs.stdForm.formModel, index, value);
  }

  setEnrollmentPrice() {
    const oldRelatedPriceParamsStr = this.getPriceParamsStringId();
    setTimeout(() => {
      const newRelatedPriceParamsStr = this.getPriceParamsStringId();
      if (newRelatedPriceParamsStr === oldRelatedPriceParamsStr) {
        const article = this.$refs.stdForm?.formModel?.article;
        let enrollment = this.$refs.stdForm?.formModel;

        if (enrollment.activity_id) {
          enrollment = {
            ...enrollment,
            activity: {
              id: enrollment.activity_id,
            },
          };
        }

        if (article) {
          const basePath = this.$bpcApiRouteHelper.getEntityPath('enrollments');
          this.$bpcApi
            .post(basePath + '/enrollment-price', {
              article: article,
              enrollment: enrollment,
            })
            .subscribe(
              res => {
                this.$set(
                  this.$refs.stdForm?.formModel,
                  'enrollment_price',
                  Number(res.data).toFixed(2),
                );
              },
              error => {
                console.log(error);
              },
            );
        }
      }
    }, 600);
  }

  // Bulk soft delete modal (move to trash)
  showBulkMoveToTrashModal = false;
  onBulkSoftDelete() {
    this.showBulkMoveToTrashModal = true;
  }
  onCLoseBulkSoftDelete() {
    this.showBulkMoveToTrashModal = false;
  }
  onSuccessSoftDelete() {
    this.$refs.softDeleteConfirmationModal.onCloseModal();
    this.$refs.provider.getEntities();
  }
  // Here, put the same fields as you put in dataToShowForBulkTrashedEntity in your entity Service
  bulkMoveToTrashDataToShow = [['code', 'article'], ['date'], ['contact']];

  // Bulk restore modal (recover)

  showBulkRecoverModal = false;

  onBulkRecover() {
    this.showBulkRecoverModal = true;
  }

  onCLoseBulkRecover() {
    this.showBulkRecoverModal = false;
  }

  onSuccessBulkRecover() {
    this.$refs.provider.getEntities();
  }

  bulkRecoverDataToShow = [['code', 'article'], ['date'], ['contact']];

  get rowActions() {
    return {
      ...this.beforeDefaultRowActions,
      ...this.defaultRowActions,
      ...this.customRowActions,
    };
  }

  public beforeDefaultRowActions = {
    payProduct: {
      activated: false,
      type: 'action',
      icon: 'credit-card',
      show: false,
      event: 'pay-product',
      text: 'pay',
      permissions: ['pay_product'],
    },
  };

  get availableToPosCheckout(): boolean {
    return (
      this.$authorizationService.hasPermissionForAction('pos_sessions', 'pos_checkout', true) &&
      this.$store.getters['pos/openPosSession'] &&
      this.entity?.orders &&
      this.entity?.orders.some(order => order.status === 'waiting')
    );
  }

  get availableToAddPayment(): boolean {
    return (
      this.entity?.orders &&
      this.entity?.orders.some(
        order =>
          order.status === 'waiting' ||
          order.status === 'shopping_cart' ||
          order.status === 'invoiced' ||
          order.status === 'payment_process',
      )
    );
  }

  onFormClickToPay() {
    this.ordersAvailableToPosCheckout = this.entity?.orders;
    this.selectedOrdersToPosCheckout = this.entity?.orders;
    this.selectedContactIdToPosCheckout = this.entity?.contact_id;
    this.selectedPriceCategoryIdToPosCheckout = this.entity?.contact?.price_category_id;
    this.selectedInvoiceIdToPosCheckout =
      this.$store.getters['pos/currentTicket']?.status === 'to_pay'
        ? this.$store.getters['pos/currentTicket']
        : null;
    this.openExportPosCheckoutModal();
  }

  onOpenPaymentModal(entity) {
    this.showPaymentModal = true;
  }

  onCloseAddpaymentModal() {
    this.showPaymentModal = false;
  }
  onPaymentCreated() {
    this.showPaymentModal = false;
    this.$bpcMessage.showSuccessMessage({ text: 'global.generic.successful_action' });
    this.i++; // refresh form
    this.$refs.provider.getEntities(); // refresh table
  }
}
